import { useEffect, useState } from "react";
import { Link, Typography } from "@material-ui/core";
import { EmptyState, InfoCard, MarkdownContent, Progress } from "@backstage/core-components";
import { useEntity } from "@backstage/plugin-catalog-react";
import { getGitlabProjectApiPath, getGitlabProjectSlug, getGitlabReadmeApiPath } from "./gitlab";

type GitlabProject = {
  default_branch: string;
  web_url: string;
};

type ReadmeState = {
  loading: boolean;
  error?: string;
  content?: string;
  projectWebUrl?: string;
  defaultBranch?: string;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return "Unknown error";
};

export const GitlabReadmeCard = () => {
  const { entity } = useEntity();
  const projectSlug = getGitlabProjectSlug(entity);

  const [state, setState] = useState<ReadmeState>({
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!projectSlug) {
        setState({
          loading: false,
          error: "gitlab.com/project-slug annotation is missing",
        });
        return;
      }

      try {
        const projectResponse = await fetch(getGitlabProjectApiPath(projectSlug));
        if (!projectResponse.ok) {
          throw new Error(`Project request failed: ${projectResponse.status}`);
        }

        const project = (await projectResponse.json()) as GitlabProject;
        const defaultBranch = project.default_branch || "master";

        const readmeResponse = await fetch(getGitlabReadmeApiPath(projectSlug, defaultBranch));
        if (!readmeResponse.ok) {
          throw new Error(`README request failed: ${readmeResponse.status}`);
        }

        const readmeContent = await readmeResponse.text();

        if (!cancelled) {
          setState({
            loading: false,
            content: readmeContent,
            projectWebUrl: project.web_url,
            defaultBranch,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            loading: false,
            error: getErrorMessage(error),
          });
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [projectSlug]);

  if (state.loading) {
    return (
      <InfoCard title="README.md">
        <Progress />
      </InfoCard>
    );
  }

  if (state.error) {
    return (
      <InfoCard title="README.md">
        <EmptyState title="Unable to load README.md" missing="info" description={state.error} />
      </InfoCard>
    );
  }

  if (!state.content || !state.projectWebUrl || !state.defaultBranch) {
    return (
      <InfoCard title="README.md">
        <EmptyState
          title="README.md is empty"
          missing="data"
          description="README.md was loaded but no content was returned."
        />
      </InfoCard>
    );
  }

  return (
    <InfoCard title="README.md">
      <Typography variant="body2" color="textSecondary">
        Source:{" "}
        <Link
          href={`${state.projectWebUrl}/-/blob/${state.defaultBranch}/README.md`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {projectSlug}
        </Link>
      </Typography>
      <MarkdownContent content={state.content} />
    </InfoCard>
  );
};
